import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Trash2, Check, X, Pencil, ScanLine, Camera } from 'lucide-react';
import {
  getNutritionDiary, getLatestNutritionProfileReport,
  addNutritionEntry, updateNutritionEntry, deleteNutritionEntry, lookupBarcode,
  getPlayerMealToday, logMealScoop,
} from '../../api';
import BarcodeScanner from '../../components/BarcodeScanner';
import NutritionLabelPhotoScan from '../../components/NutritionLabelPhotoScan';
import AddFoodModal from '../../components/AddFoodModal';
import { round1, SourceTag, FoodAutocomplete, QuantityPicker } from '../../components/FoodPicker';

// Own copy of the meal list (same convention as the rest of the codebase).
const MEALS = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch',     label: 'Lunch' },
  { id: 'dinner',    label: 'Dinner' },
  { id: 'snack',     label: 'Snacks' },
];

// LOCAL calendar date (not UTC) — same reasoning as PlayerDataContext's tod().
const tod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
function shiftDate(str, delta) {
  const d = new Date(str + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDay(str) {
  return new Date(str + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}
// round1, SourceTag, FoodAutocomplete, QuantityPicker: imported from ../../components/FoodPicker
// (extracted there so the chef recipe editor can reuse the exact same food search / quantity UI).
const CARD = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', marginBottom: 12 };
const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});

// ── One logged line ─────────────────────────────────────────────────────────
function EntryRow({ entry, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [grams, setGrams] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // A logged scoop/unit count from the chef's planned meal isn't a real diary_entries row — it's
  // derived live from nutrition_meal_scoop_logs (server.staging.js's computeLoggedScoopEntries),
  // so it has no id this page's PUT/DELETE routes could act on. Editing/removing it happens via
  // the count field on the "Meal of the day" card instead.
  const isChefMeal = entry.food_source === 'chef_meal';

  const save = async () => {
    if (!(grams > 0)) { setErr('Enter an amount greater than 0'); return; }
    setBusy(true); setErr('');
    try { await onUpdate(entry.id, { amount_grams: grams }); setEditing(false); }
    catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div style={{ padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          {entry.food_name}<SourceTag source={entry.food_source} />
        </div>
        <QuantityPicker food={entry} initialGrams={entry.amount_grams} onChange={setGrams} />
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button style={btn(true)} onClick={save} disabled={busy}><Check size={13} /> Save</button>
          <button style={btn(false)} onClick={() => { setEditing(false); setErr(''); }}><X size={13} /></button>
        </div>
        {err && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{err}</div>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {entry.food_name}<SourceTag source={entry.food_source} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          {isChefMeal ? `${entry.scoop_count} ${entry.unit_label}${entry.scoop_count === 1 ? '' : 's'} · ` : ''}
          {entry.amount_grams} g
          {entry.grams_per_portion ? ` · ${round1(entry.amount_grams / entry.grams_per_portion)} portion` : ''}
          {' '}· {entry.kcal} kcal · P {entry.protein_g} / C {entry.carbs_g} / F {entry.fat_g} g
        </div>
      </div>
      {!isChefMeal && (
        <>
          <button style={{ ...btn(false), padding: 6 }} onClick={() => setEditing(true)} title="Edit"><Pencil size={12} /></button>
          <button style={{ ...btn(false, '#ef4444'), padding: 6 }} onClick={() => onDelete(entry.id)} title="Delete"><Trash2 size={12} /></button>
        </>
      )}
    </div>
  );
}

// ── One meal section (Breakfast / Lunch / …) ────────────────────────────────
// plannedRecipes: the chef's day-recipes assigned to THIS meal moment (already filtered by the
// caller) — shown at the top of the section, with their scoop/unit logging inputs right there,
// instead of on a separate "Meal of the day" card the player had to go find first.
function MealSection({ meal, entries, plannedRecipes, playerId, onLogged, onAdd, onUpdate, onDelete }) {
  const [adding, setAdding] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [picked, setPicked] = useState(null);
  const [grams, setGrams] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Set only when the scanned barcode genuinely has no product data anywhere (not_found /
  // incomplete) — offers the photo-of-the-label fallback. A transient lookup problem (rate
  // limit, network) gets a plain retry message instead; that's not "unrecognised".
  const [unrecognizedBarcode, setUnrecognizedBarcode] = useState(null);
  const [showPhotoScan, setShowPhotoScan] = useState(false);
  // Not barcode-anchored at all — a product with no barcode, found nowhere yet. Same shared
  // nutrition_foods database as the barcode/photo-scan paths above (source='player'), so other
  // players/chefs/admins find it afterwards without re-entering it themselves.
  const [showAddFood, setShowAddFood] = useState(false);

  const rows = entries.filter(e => e.meal_type === meal.id);
  // A logged scoop/unit count already shows (and is editable) in the PlannedMealBlock above —
  // its diary-merge line (computeLoggedScoopEntries) still counts toward kcal, just not shown
  // twice as a separate manual-looking row here.
  const visibleRows = rows.filter(e => e.food_source !== 'chef_meal');
  const kcal = rows.reduce((s, e) => s + e.kcal, 0);

  const reset = () => { setAdding(false); setPicked(null); setGrams(null); setErr(''); setUnrecognizedBarcode(null); };

  const save = async () => {
    if (!picked) { setErr('Pick a food first'); return; }
    if (!(grams > 0)) { setErr('Enter an amount greater than 0'); return; }
    setBusy(true); setErr('');
    try { await onAdd({ meal_type: meal.id, food_id: picked.id, amount_grams: grams }); reset(); }
    catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  const onScanned = async (barcode) => {
    setScanning(false);
    setAdding(true); setPicked(null); setErr('Looking up barcode…'); setUnrecognizedBarcode(null);
    try {
      const r = await lookupBarcode(barcode);
      if (r.ok && r.food) { setPicked(r.food); setErr(''); return; }
      if (r.status === 429) setErr('Barcode service is busy — wait a moment and try again, or search manually.');
      else if (r.error === 'incomplete') { setErr('Found the product but it has no usable nutrition data.'); setUnrecognizedBarcode(barcode); }
      else if (r.error === 'not_found') { setErr('Barcode not recognised.'); setUnrecognizedBarcode(barcode); }
      else setErr('Barcode lookup failed — search for the food manually.');
    } catch {
      setErr('Barcode lookup failed — search for the food manually.');
    }
  };

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 700 }}>{meal.label}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{kcal} kcal</span>
      </div>

      {plannedRecipes.map(r => (
        <PlannedMealBlock key={r.day_recipe_id} recipe={r} playerId={playerId} onLogged={onLogged} />
      ))}

      {visibleRows.length === 0 && plannedRecipes.length === 0 && !adding && (
        <div style={{ fontSize: 12, color: 'var(--text-dimmed)', marginBottom: 8 }}>Nothing added yet.</div>
      )}
      {visibleRows.map(e => <EntryRow key={e.id} entry={e} onUpdate={onUpdate} onDelete={onDelete} />)}

      {adding ? (
        <div style={{ padding: '10px', background: 'var(--bg-app)', borderRadius: 8, marginTop: 4 }}>
          {!picked ? (
            <>
              <FoodAutocomplete onPick={f => { setPicked(f); setErr(''); }} />
              <button type="button" onClick={() => setShowAddFood(true)} style={{ ...btn(false), marginTop: 6, fontSize: 11, padding: '4px 8px' }}>
                Niet gevonden? Nieuw product toevoegen…
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                {picked.name}<SourceTag source={picked.source} />
                <button onClick={() => { setPicked(null); setGrams(null); }} style={{ ...btn(false), padding: '2px 8px', marginLeft: 8, fontSize: 11 }}>change</button>
              </div>
              <QuantityPicker food={picked} onChange={setGrams} />
            </>
          )}
          {err && <div style={{ fontSize: 11, color: err.startsWith('Looking') ? 'var(--text-muted)' : '#ef4444', marginTop: 6 }}>{err}</div>}
          {unrecognizedBarcode && !picked && (
            <button style={{ ...btn(false, '#a855f7'), marginTop: 8 }} onClick={() => setShowPhotoScan(true)}>
              <Camera size={13} /> Take a photo of the nutrition label
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={btn(true)} onClick={save} disabled={busy || !picked || !(grams > 0)}>{busy ? 'Saving…' : 'Add'}</button>
            <button style={btn(false)} onClick={reset}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => { setErr(''); setAdding(true); }}>
            <Plus size={13} /> Add
          </button>
          <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setScanning(true)}>
            <ScanLine size={13} /> Scan
          </button>
        </div>
      )}

      {scanning && <BarcodeScanner onDetect={onScanned} onClose={() => setScanning(false)} />}
      {showPhotoScan && (
        <NutritionLabelPhotoScan
          barcode={unrecognizedBarcode}
          onClose={() => setShowPhotoScan(false)}
          onSaved={(food) => { setPicked(food); setErr(''); setUnrecognizedBarcode(null); setShowPhotoScan(false); }}
        />
      )}
      {showAddFood && (
        <AddFoodModal
          showClassify={false}
          onClose={() => setShowAddFood(false)}
          onAdded={(food) => { setPicked(food); setErr(''); setShowAddFood(false); }}
        />
      )}
    </div>
  );
}

// ── Action plan set by staff — read-only for the player ─────────────────────
// Mirrors the admin Nutrition screen's Actieplan section — shows the MOST RECENT profile report
// (staff can save a new dated report any time; earlier ones stay in the admin history but are
// never shown here). Renders nothing at all when there's no plan yet, rather than an empty card.
function ActionPlanCard({ playerId }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getLatestNutritionProfileReport(playerId)
      .then(p => { if (!cancelled) setItems((p.action_plan || []).filter(x => String(x || '').trim())); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [playerId]);

  if (!items || items.length === 0) return null;
  return (
    <div style={CARD}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Action plan</div>
      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)' }}>
        {items.map((text, i) => <li key={i} style={{ marginBottom: 4 }}>{text}</li>)}
      </ol>
    </div>
  );
}

// ── Day total vs goal ───────────────────────────────────────────────────────
function GoalBar({ label, value, goal, unit }) {
  const pct = goal ? Math.round((value / goal) * 100) : null;
  const width = pct == null ? 0 : Math.min(100, pct);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ color: 'var(--text-muted)' }}>
          {value}{unit}{goal != null ? ` / ${goal}${unit}` : ''}{pct != null ? ` · ${pct}%` : ''}
        </span>
      </div>
      {goal != null && (
        <div style={{ height: 6, background: 'var(--gauge-track)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${width}%`, background: pct > 100 ? '#ef4444' : pct === 100 ? '#10b981' : '#3b82f6', borderRadius: 99 }} />
        </div>
      )}
    </div>
  );
}

// ── Meal of the day — scoop logging (groundwork) ─────────────────────────────
// Shows whatever the chef assigned for this date's Plate (server.staging.js's
// GET /api/nutrition/player/meal-today) and lets the player log a count per scoop-station group
// the recipe actually has (server-computed scoop_groups_present) — the chef partitions the 4
// roles into however many groups they like (RecipeEditor.js's Opscheplepel-indeling), so this is
// never just a binary "one count or one per role" anymore. Each group shows a short, everyday
// English description of what's actually in it that day (server-generated via Claude, cached),
// not the literal NEVO product names. A group normally counts in "scoops", but the chef can set
// a specific named unit per group instead (e.g. "steak", "sweet potato") — group.unit_name.
// No gram/macro conversion is shown yet: scoop_grams (admin-set, per role) is still null until
// the real values are filled in — logging the count now means only the "connect the conversion"
// step is left once they are.
const SCOOP_ROLE_LABELS = { protein: 'Protein source', carbs: 'Carb source', vegetable: 'Vegetable source', other: 'Other' };

// Admin-configurable board label (e.g. "Bord 5" — Settings screens stay Dutch on purpose) shown
// to a player as "Plate 5" instead — a display-only transform, never touches the stored value.
function playerPlateLabel(board) {
  if (!board) return board;
  const m = String(board).match(/^bord\s*(.*)$/i);
  return m ? `Plate ${m[1]}`.trim() : board;
}

function pluralizeUnit(word, count) {
  if (!word) return word;
  return Number(count) === 1 ? word : (/[sxz]$|[cs]h$/i.test(word) ? `${word}es` : `${word}s`);
}

// perUnit (server-computed) is always present for a named-unit group (derived straight from this
// group's own ingredient grams/macros ÷ recipe_yield) and present for a plain scoop group too as
// soon as the admin has filled in a grams-per-scoop value for at least one of its roles
// (nutrition_scoop_grams) — server.staging.js's computeGroupPerCount. Only a scoop group whose
// roles have no admin value at all yet comes back with perUnit null, which is when the "not set
// up yet" caveat below applies.
function ScoopInput({ dayRecipeId, groupKey, description, roleLabel, unitName, perUnit, initialCount, playerId, onSaved }) {
  const [count, setCount] = useState(initialCount != null ? String(initialCount) : '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (count === '' || Number(count) < 0) return;
    setSaving(true); setSaved(false);
    try {
      await logMealScoop(dayRecipeId, groupKey, Number(count), playerId);
      setSaved(true);
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  const unitWord = pluralizeUnit(unitName || 'scoop', count || 0);

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{description || roleLabel}</div>
      {description && <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginBottom: 4 }}>{roleLabel}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        <input type="number" min="0" step="1" value={count} onChange={e => { setCount(e.target.value); setSaved(false); }}
          style={{ ...inputStyle, width: 64 }} placeholder="0" />
        <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>{unitWord}</span>
        <button style={{ ...btn(false), padding: '5px 9px' }} disabled={saving || count === ''} onClick={save}>
          {saving ? '…' : saved ? <Check size={13} /> : 'Log'}
        </button>
      </div>
      {perUnit && (
        <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 3 }}>
          ≈ {perUnit.grams}g per {unitWord.replace(/s$/, '')} · {perUnit.kcal} kcal · P {perUnit.protein_g}g / C {perUnit.carbs_g}g / F {perUnit.fat_g}g
        </div>
      )}
    </div>
  );
}

// One recipe the chef planned for this meal moment, shown right inside that meal's own section
// (MealSection) — a player logging their scoop/unit count here needs no separate card or tab.
function PlannedMealBlock({ recipe, playerId, onLogged }) {
  const hasUnconvertedScoopGroup = recipe.scoop_groups_present.some(g => !g.unit_name && !g.per_unit);
  return (
    <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: 10, marginBottom: 8, border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmed)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
        Planned meal
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{recipe.name}</div>
      {recipe.scoop_groups_present.map(group => (
        <ScoopInput key={group.key} dayRecipeId={recipe.day_recipe_id} groupKey={group.key}
          description={group.description}
          roleLabel={group.roles.map(role => SCOOP_ROLE_LABELS[role]).join(' + ')}
          unitName={group.unit_name}
          perUnit={group.per_unit}
          initialCount={recipe.logged[group.key]} playerId={playerId} onSaved={onLogged} />
      ))}
      {hasUnconvertedScoopGroup && (
        <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 2 }}>
          Scoop counts — the gram/macro conversion isn&apos;t set up yet.
        </div>
      )}
    </div>
  );
}

export default function PlayerNutrition({ playerRecord }) {
  const navigate = useNavigate();
  const [date, setDate] = useState(tod());
  const [data, setData] = useState({
    entries: [],
    totals: { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, saturated_fat_g: 0, unsaturated_fat_g: 0 },
    goal: null,
    effectiveGoals: { kcal_goal: null, protein_g_goal: null, carbs_g_goal: null, fat_g_goal: null },
  });
  const [loading, setLoading] = useState(true);
  // The chef's planned meal(s) for this date, split per meal moment below so each MealSection
  // can show (and let the player log against) only what's actually assigned to it.
  const [mealPlan, setMealPlan] = useState(null);

  // showLoading is only true for the genuine first load of a date (nothing sensible to show yet,
  // so the spinner replacing MEALS.map(...) is correct there). Every other refresh — after
  // logging a scoop/unit count, adding/editing/deleting a manual entry — must NOT flip the
  // top-level `loading` flag: that flag gates the whole MEALS.map(...) block below (including
  // every PlannedMealBlock/ScoopInput), so toggling it on a background refresh was unmounting and
  // remounting the entire block the player just interacted with — the "Log" button's own count
  // appeared to vanish (it hadn't; the block briefly became the "Loading…" placeholder instead,
  // then came back). Keeping `loading` untouched here lets the existing content stay on screen
  // (and each row's own busy/disabled state show the refresh instead) while data/mealPlan update
  // underneath it.
  const load = useCallback(async (showLoading = false) => {
    if (!playerRecord?.id) { setLoading(false); return; }
    if (showLoading) setLoading(true);
    try { setData(await getNutritionDiary(playerRecord.id, date)); }
    catch { /* leave last-loaded data in place */ }
    finally { if (showLoading) setLoading(false); }
  }, [playerRecord?.id, date]);

  const loadMealPlan = useCallback(() => {
    if (!playerRecord?.id) return;
    getPlayerMealToday(playerRecord.id, date).then(setMealPlan).catch(() => setMealPlan({ recipes: [] }));
  }, [playerRecord?.id, date]);

  useEffect(() => { load(true); }, [load]);
  useEffect(() => { loadMealPlan(); }, [loadMealPlan]);
  // Logging a scoop/unit count changes both the diary totals (via the server-side merge) and
  // the plan's own "already logged" state — both need a fresh fetch afterwards, quietly (see
  // `load` above) so the block the player just logged against stays visible and editable.
  const reloadAfterLogging = () => { load(); loadMealPlan(); };

  const addEntry = async (payload) => {
    await addNutritionEntry({ player_id: playerRecord.id, entry_date: date, ...payload });
    load();
  };
  const updateEntry = async (id, patch) => { await updateNutritionEntry(id, patch); load(); };
  const removeEntry = async (id) => { await deleteNutritionEntry(id); load(); };

  const { totals } = data;
  const eg = data.effectiveGoals || {};
  const plannedRecipes = mealPlan?.recipes || [];

  return (
    <div style={{ padding: '14px 14px 24px', maxWidth: 640, margin: '0 auto' }}>
      <button onClick={() => navigate('/player/forms')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0, fontFamily: 'inherit' }}>
        <ChevronLeft size={16} /> Back
      </button>

      <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>Food Tracker</div>

      {/* Date picker */}
      <div style={{ ...CARD, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(d => shiftDate(d, -1))}><ChevronLeft size={15} /></button>
        <input type="date" value={date} onChange={e => setDate(e.target.value || tod())}
          style={{ ...inputStyle, width: 'auto', flex: 1, textAlign: 'center' }} />
        <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(d => shiftDate(d, 1))}><ChevronRight size={15} /></button>
        <button style={btn(date === tod())} onClick={() => setDate(tod())}>Today</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-dimmed)', margin: '-4px 0 12px 2px' }}>
        {fmtDay(date)}
        {mealPlan?.label && (
          <span> · {mealPlan.label}{mealPlan.board ? ` — ${playerPlateLabel(mealPlan.board)}` : ''}</span>
        )}
      </div>

      {playerRecord?.id && <ActionPlanCard playerId={playerRecord.id} />}

      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <>
          {MEALS.map(m => (
            <MealSection key={m.id} meal={m} entries={data.entries}
              plannedRecipes={plannedRecipes.filter(r => r.meal_type === m.id)}
              playerId={playerRecord?.id} onLogged={reloadAfterLogging}
              onAdd={addEntry} onUpdate={updateEntry} onDelete={removeEntry} />
          ))}

          {/* Day total */}
          <div style={{ ...CARD, marginTop: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Day total</div>
            <GoalBar label="Calories" value={totals.kcal} goal={eg.kcal_goal} unit=" kcal" />
            <GoalBar label="Protein"  value={totals.protein_g} goal={eg.protein_g_goal} unit=" g" />
            <GoalBar label="Carbs"    value={totals.carbs_g} goal={eg.carbs_g_goal} unit=" g" />
            <GoalBar label="Fat"      value={totals.fat_g} goal={eg.fat_g_goal} unit=" g" />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', padding: '0 0 3px 12px' }}>
              <span>— saturated</span><span>{totals.saturated_fat_g ?? 0} g</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', padding: '0 0 8px 12px' }}>
              <span>— unsaturated</span><span>{totals.unsaturated_fat_g ?? 0} g</span>
            </div>
            {eg.kcal_goal == null && eg.fat_g_goal == null && (
              <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 4 }}>
                No calorie / fat goal set by staff yet.
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8 }}>
              Protein &amp; carb goals are calculated from your body weight and the day type in
              the week schedule.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
